import { useState } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { IMAGE_URL } from '../../../path';
import Select from "react-select";

type Props = {
    product?: any;
    ranges: any[];
    bestServedWith: any[];
    onClose: () => void;
    onSave: (data: any) => void;
};

export default function ProductModal({
                                         product,
                                         ranges,
                                         bestServedWith,
                                         onClose,
                                         onSave,
                                     }: Props)
{
    const [rangeId, setRangeId] =
        useState(
            product?.range_id || ''
        );

    const [name, setName] =
        useState(
            product?.name || ''
        );

    const [flavour, setFlavour] =
        useState(
            product?.flavour || ''
        );

    const [coverImage, setCoverImage] =
        useState<File | null>(
            null
        );
    const [featured, setFeatured] =
        useState(
            product?.featured ?? false
        );

    const [productImage, setProductImage] =
        useState<File | null>(
            null
        );

    const bestServedWithOptions = bestServedWith.map(
        (item: any) => ({
            value: item.id,
            label: item.name,
        })
    );

    const [selectedBestServedWith, setSelectedBestServedWith] =
        useState<any[]>(
            product?.best_served_with?.map((item: any) => ({
                value: item.id,
                label: item.name,
            })) || []
        );

    const [coverPreview, setCoverPreview] =
        useState(
            product?.cover_image || ''
        );

    const [productPreview, setProductPreview] =
        useState(
            product?.product_image || ''
        );

    const editor = useEditor({

        extensions: [
            StarterKit,
        ],

        content:
            product?.description || '',

    });
    const [sizesAvailable, setSizesAvailable] =
        useState(
            product?.sizes_available?.join(', ') || ''
        );

    const [isActive, setIsActive] =
        useState(
            product?.is_active ?? true
        );

    const getImageUrl = (
        path?: string
    ) => {

        if (!path) {
            return '';
        }

        if (
            path.startsWith(
                'http'
            ) ||
            path.startsWith(
                'blob:'
            )
        )
        {
            return path;
        }

        return `${IMAGE_URL}${path}`;
    };

    const handleSubmit = (
        e: React.FormEvent
    ) => {

        e.preventDefault();

        const formData =
            new FormData();

        formData.append(
            'range_id',
            String(
                rangeId
            )
        );

        formData.append(
            'name',
            name
        );

        formData.append(
            'flavour',
            flavour
        );

        formData.append(
            'description',
            editor?.getHTML() || ''
        );

        formData.append(
            'is_active',
            isActive
                ? '1'
                : '0'
        );

        formData.append(
            'featured',
            featured
                ? '1'
                : '0'
        );

        sizesAvailable
            .split(',')
            .map(
                (size: any) =>
                    size.trim()
            )
            .filter(Boolean)
            .forEach(
                (size: any) => {

                    formData.append(
                        'sizes_available[]',
                        size
                    );

                }
            );

        if (
            coverImage
        ) {

            formData.append(
                'cover_image',
                coverImage
            );
        }

        if (
            productImage
        ) {

            formData.append(
                'product_image',
                productImage
            );
        }

        selectedBestServedWith.forEach((item: any) => {
            formData.append(
                "best_served_with[]",
                String(item.value)
            );
        });

        onSave(
            formData
        );
    };

    return (

        <div
            className="modal-overlay"
            onClick={onClose}
        >

            <div
                className="modal-box"
                onClick={(e) => e.stopPropagation()}
            >

                <h2>

                    {product
                        ? 'Edit Product'
                        : 'Add Product'}

                </h2>

                <form
                    className="modal-form"
                    onSubmit={
                        handleSubmit
                    }
                >

                    <label>
                        Range
                    </label>

                    <select
                        value={
                            rangeId
                        }
                        onChange={(e) =>
                            setRangeId(
                                e.target.value
                            )
                        }
                        required
                    >


                        <option value="">
                            Select Range
                        </option>

                        {ranges.map(
                            range => (

                                <option
                                    key={
                                        range.id
                                    }
                                    value={
                                        range.id
                                    }
                                >
                                    {
                                        range.display_name
                                    }
                                </option>

                            )
                        )}


                    </select>

                    <label>
                        Name
                    </label>

                    <input
                        type="text"
                        value={name}
                        onChange={(e) =>
                            setName(
                                e.target.value
                            )
                        }
                        required
                    />

                    <label>
                        Flavour
                    </label>

                    <input
                        type="text"
                        value={flavour}
                        onChange={(e) =>
                            setFlavour(
                                e.target.value
                            )
                        }
                        required
                    />

                    <label>
                        Best Served With
                    </label>

                    <Select
                        isMulti
                        options={bestServedWithOptions}
                        value={selectedBestServedWith}
                        onChange={(value: any) =>
                            setSelectedBestServedWith(
                                value as any[]
                            )
                        }
                    />

                    <label>
                        Cover Image
                    </label>

                    {coverPreview && (

                        <img
                            src={
                                getImageUrl(
                                    coverPreview
                                )
                            }
                            alt=""
                            className="image-preview"
                        />

                    )}

                    <p className="field-hint">
                        Maximum file size: 2MB
                    </p>

                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {

                            const file =
                                e.target.files?.[0];

                            if (!file) {
                                return;
                            }

                            if (
                                file.size >
                                2 * 1024 * 1024
                            ) {

                                alert(
                                    'Image must be 2MB or smaller.'
                                );

                                return;
                            }

                            setCoverImage(
                                file
                            );

                            setCoverPreview(
                                URL.createObjectURL(
                                    file
                                )
                            );

                        }}
                    />

                    <label>
                        Product Image
                    </label>

                    {productPreview && (

                        <img
                            src={
                                getImageUrl(
                                    productPreview
                                )
                            }
                            alt=""
                            className="image-preview"
                        />

                    )}

                    <p className="field-hint">
                        Maximum file size: 2MB
                    </p>

                    <input
                        type="file"
                        accept="image/*"
                        onChange={(e) => {

                            const file =
                                e.target.files?.[0];

                            if (!file) {
                                return;
                            }

                            if (
                                file.size >
                                2 * 1024 * 1024
                            ) {

                                alert(
                                    'Image must be 2MB or smaller.'
                                );

                                return;
                            }

                            setProductImage(
                                file
                            );

                            setProductPreview(
                                URL.createObjectURL(
                                    file
                                )
                            );

                        }}
                    />

                    <label>
                        Description
                    </label>

                    <div className="tiptap-editor">

                        <div className="editor-toolbar">

                            <button
                                type="button"
                                onClick={() =>
                                    editor
                                        ?.chain()
                                        .focus()
                                        .toggleBold()
                                        .run()
                                }
                            >
                                Bold
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    editor
                                        ?.chain()
                                        .focus()
                                        .toggleItalic()
                                        .run()
                                }
                            >
                                Italic
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    editor
                                        ?.chain()
                                        .focus()
                                        .toggleBulletList()
                                        .run()
                                }
                            >
                                List
                            </button>

                            <button
                                type="button"
                                onClick={() =>
                                    editor
                                        ?.chain()
                                        .focus()
                                        .toggleHeading({
                                            level: 2,
                                        })
                                        .run()
                                }
                            >
                                H2
                            </button>

                        </div>

                        <EditorContent
                            editor={editor}
                        />

                    </div>

                    <label>
                        Sizes Available
                    </label>

                    <input
                        type="text"
                        value={
                            sizesAvailable
                        }
                        onChange={(e) =>
                            setSizesAvailable(
                                e.target.value
                            )
                        }
                        placeholder="250g, 500g, 1kg"
                    />

                    <label>
                        Active
                    </label>

                    <select
                        value={
                            isActive
                                ? '1'
                                : '0'
                        }
                        onChange={(e) =>
                            setIsActive(
                                e.target.value ===
                                '1'
                            )
                        }
                    >

                        <option value="1">
                            Yes
                        </option>

                        <option value="0">
                            No
                        </option>

                    </select>

                    <label>
                        Featured Product
                    </label>

                    <select
                        value={
                            featured
                                ? '1'
                                : '0'
                        }
                        onChange={(e) =>
                            setFeatured(
                                e.target.value ===
                                '1'
                            )
                        }
                    >

                        <option value="0">
                            No
                        </option>

                        <option value="1">
                            Yes
                        </option>

                    </select>

                    <div className="modal-actions">

                        <div className="left-actions">

                            <button
                                type="button"
                                className="cancel-btn"
                                onClick={
                                    onClose
                                }
                            >
                                Cancel
                            </button>

                        </div>

                        <div className="right-actions">

                            <button
                                type="submit"
                                className="action-btn"
                            >
                                Save
                            </button>

                        </div>

                    </div>

                </form>

            </div>

        </div>

    );
}
